import React, { useState } from 'react';
import { api } from '@/lib/api';
import { VOTING } from '@/constants/testIds';
import { VOTE_META } from '@/lib/personalities';
import { Check, Star, MinusCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ICONS = { must: Star, nice: Check, skip: MinusCircle };

export default function VotingButtons({
    ideaId,
    tripId,
    initialVote,
    breakdown = { must: 0, nice: 0, skip: 0 },
    score = 0,
    onVoted,
    compact = false,
}) {
    const [vote, setVote] = useState(initialVote || null);
    const [pending, setPending] = useState(null);

    const cast = async (v, e) => {
        e?.stopPropagation?.();
        e?.preventDefault?.();
        if (!tripId) return;
        const toggle = vote === v;
        setPending(v);
        try {
            if (toggle) {
                await api.delete(`/ideas/${ideaId}/vote`, { params: { trip_id: tripId } });
                setVote(null);
                onVoted?.(null);
            } else {
                await api.post(`/ideas/${ideaId}/vote`, { trip_id: tripId, vote: v });
                setVote(v);
                onVoted?.(v);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Vote failed');
        } finally {
            setPending(null);
        }
    };

    const Pill = ({ v }) => {
        const meta = VOTE_META[v];
        const Icon = ICONS[v];
        const active = vote === v;
        const isPending = pending === v;
        return (
            <button
                type="button"
                onClick={(e) => cast(v, e)}
                data-testid={VOTING[`${v}Button`]}
                disabled={isPending}
                className={`inline-flex items-center justify-center gap-1.5 rounded-full transition border ${compact ? 'h-8 px-3 text-xs' : 'h-9 px-3.5 text-sm'} ${active
                    ? 'text-white border-transparent shadow-sm'
                    : 'text-[var(--ns-text2)] bg-white border-[var(--ns-border)] hover:border-[var(--ns-borderStrong)]'}`}
                style={active ? { background: meta.color } : undefined}
            >
                {isPending ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />}
                <span className="font-medium">{meta.label}</span>
            </button>
        );
    };

    return (
        <div
            data-testid={VOTING.inlineButtons}
            className={`flex items-center ${compact ? 'gap-1.5' : 'gap-2'} flex-wrap`}
            onClick={(e) => e.stopPropagation()}
        >
            <Pill v="must" />
            <Pill v="nice" />
            <Pill v="skip" />
            {!compact && (
                <div className="ml-auto flex items-center gap-2 text-xs text-[var(--ns-text3)]">
                    <span data-testid={VOTING.breakdown} className="inline-flex items-center gap-1.5">
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: VOTE_META.must.color }} /> {breakdown.must}</span>
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: VOTE_META.nice.color }} /> {breakdown.nice}</span>
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: VOTE_META.skip.color }} /> {breakdown.skip}</span>
                    </span>
                    <span className="w-px h-3 bg-[var(--ns-border)]" />
                    <span data-testid={VOTING.score} className="inline-flex items-center gap-1">
                        <span className="font-semibold text-[var(--ns-text)]">{score}</span> pts
                    </span>
                </div>
            )}
        </div>
    );
}
